import React, { useEffect, lazy, Suspense } from "react";
import { Capacitor } from "@capacitor/core";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import LanguageDialogs from "./components/LanguageDialogs";
import UsernameSetupDialog from "./components/auth/UsernameSetupDialog";
import { PushNotificationInitializer } from "./components/PushNotificationInitializer";
import { supabase } from "./integrations/supabase/client";
import { toast } from "sonner";
import { ThemeProvider } from "next-themes";

// Lazy load pages for better performance
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Profile = lazy(() => import("./pages/Profile"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Analytics = lazy(() => import("./pages/Analytics"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PublicReport = lazy(() => import("./pages/PublicReport"));
const PublicCity = lazy(() => import("./pages/PublicCity"));
const PublicCitiesIndex = lazy(() => import("./pages/PublicCitiesIndex"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const AccessChoice = lazy(() => import("./pages/AccessChoice"));
const AppAccessChoice = lazy(() => import("./pages/AppAccessChoice"));
const MunicipalityAuth = lazy(() => import("./pages/MunicipalityAuth"));
const AdminMunicipalities = lazy(() => import("./pages/AdminMunicipalities"));
const MunicipalityDashboard = lazy(() => import("./pages/MunicipalityDashboard"));

const isNativeApp = Capacitor.isNativePlatform();

// Create a new QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: 1000,
      staleTime: 5 * 60 * 1000,      // 5 minutes - data stays fresh longer
      gcTime: 10 * 60 * 1000,        // 10 minutes - cache cleanup
      refetchOnWindowFocus: false,   // Don't refetch when window regains focus
    },
  },
});

// Ping function to keep Supabase project awake
const pingSupabase = async () => {
  console.log("=== ANDROID DEBUG: Starting Supabase ping ===");
  try {
    console.log("=== ANDROID DEBUG: About to query Supabase ===");
    const result = await supabase.from('reports').select('id').limit(1);
    console.log("=== ANDROID DEBUG: Supabase query successful ===", result);
  } catch (error) {
    console.error("=== ANDROID DEBUG: Supabase query failed ===", error);
    console.error("=== ANDROID DEBUG: Error details ===", JSON.stringify(error, null, 2));
  }
};

// Referral code handler component
function ReferralHandler() {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referralCode', refCode.toUpperCase());
      toast.success(`Codice invito ${refCode.toUpperCase()} salvato! Registrati per ricevere 50 punti bonus 🎁`);
    }
  }, [searchParams]);
  
  return null;
}

const App = () => {
  // Add global error handling for Android debugging
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error("=== ANDROID DEBUG: Global error caught ===", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("=== ANDROID DEBUG: Unhandled promise rejection ===", event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Setup ping Supabase on initial load (removed realtime setup from here)
  useEffect(() => {
    // Delay initial ping for mobile platforms to ensure network is ready
    const initialDelay = setTimeout(() => {
      pingSupabase();
    }, 2000); // 2 second delay for mobile
    
    // Set up interval to ping every 12 hours (to prevent free-tier auto-suspension)
    const pingInterval = setInterval(pingSupabase, 12 * 60 * 60 * 1000);
    
    return () => {
      clearTimeout(initialDelay);
      clearInterval(pingInterval);
    };
  }, []);

  // Google Sign-In on native platforms uses Supabase OAuth opened via
  // @capacitor/browser; the callback is handled in AuthContext via the
  // shapeit:// deep link. No native Google Auth plugin is initialized here.

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <AuthProvider>
          <LanguageProvider>
            <TooltipProvider>
              <PushNotificationInitializer />
              <Toaster />
              <Sonner />
              <LanguageDialogs />
              <UsernameSetupDialog />
              <BrowserRouter>
                <ReferralHandler />
                <Suspense fallback={
                  <div className="flex items-center justify-center min-h-dvh">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                }>
                  <Routes>
                    <Route path="/" element={isNativeApp ? <Index /> : <Landing />} />
                    <Route path="/app" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/accedi" element={<AccessChoice />} />
                    <Route path="/app/accedi" element={<AppAccessChoice />} />
                    <Route path="/comune/accedi" element={<MunicipalityAuth />} />
                    <Route path="/comune/dashboard" element={<MunicipalityDashboard />} />
                    <Route path="/admin/comuni" element={<AdminMunicipalities />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/account-settings" element={<AccountSettings />} />
                    <Route path="/analytics" element={<Analytics />} />
                    {!isNativeApp && <Route path="/r/:id" element={<PublicReport />} />}
                    {!isNativeApp && <Route path="/citta" element={<PublicCitiesIndex />} />}
                    {!isNativeApp && <Route path="/citta/:slug" element={<PublicCity />} />}
                    {!isNativeApp && <Route path="/delete-account" element={<DeleteAccount />} />}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;

